/**
 * InvoiceDetail Component
 *
 * Full invoice view with items, payment history,
 * and action buttons.
 *
 * @module components/invoices/InvoiceDetail
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getInvoice, updateInvoice, recordPayment } from '../../services/invoiceService';
import { exportInvoiceToPDF } from '../../utils/invoiceExport';
import { useAuth } from '../../contexts/AuthContext';
import PaymentModal from './PaymentModal';

const statusConfig = {
  draft: { label: 'Borrador', variant: 'secondary' },
  sent: { label: 'Enviada', variant: 'primary' },
  partial_payment: { label: 'Pago Parcial', variant: 'warning' },
  paid: { label: 'Pagada', variant: 'success' },
  overdue: { label: 'Vencida', variant: 'danger' },
  cancelled: { label: 'Cancelada', variant: 'dark' },
};

const paymentMethodLabels = {
  transfer: 'Transferencia',
  check: 'Cheque',
  cash: 'Efectivo',
  credit_card: 'Tarjeta',
  other: 'Otro',
};

const InvoiceDetail = ({ invoiceId, onBack }) => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const { data: invoiceData, isLoading, error } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: !!invoiceId,
  });

  const invoice = invoiceData?.data;

  const handleSendInvoice = async () => {
    try {
      const result = await updateInvoice(invoiceId, { status: 'sent' });
      if (result.error) throw result.error;
      toast.success('Factura marcada como enviada');
      queryClient.invalidateQueries(['invoice', invoiceId]);
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleCancelInvoice = async () => {
    try {
      const result = await updateInvoice(invoiceId, { status: 'cancelled' });
      if (result.error) throw result.error;
      toast.success('Factura cancelada');
      queryClient.invalidateQueries(['invoice', invoiceId]);
    } catch (err) {
      toast.error('Error al cancelar factura');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      if (!invoice) return;
      await exportInvoiceToPDF(invoice);
      toast.success('PDF generado');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Error al generar PDF');
    }
  };

  const handleRecordPayment = async (paymentData) => {
    try {
      setPaymentLoading(true);
      const result = await recordPayment({
        ...paymentData,
        invoice_id: invoiceId,
      });
      if (result.error) throw result.error;

      // Update invoice status based on payments
      const newPaidAmount = (invoice.paid_amount || 0) + paymentData.amount;
      const newStatus = newPaidAmount >= invoice.amount ? 'paid' : 'partial_payment';
      await updateInvoice(invoiceId, { status: newStatus });

      toast.success('Pago registrado exitosamente');
      setShowPayment(false);
      queryClient.invalidateQueries(['invoice', invoiceId]);
    } catch (err) {
      toast.error('Error al registrar pago');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !invoice) {
    return <Alert variant="danger">Error al cargar la factura</Alert>;
  }

  const si = statusConfig[invoice.status] || statusConfig.draft;

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            {onBack && (
              <Button variant="link" className="p-0" onClick={onBack}>
                <i className="bi bi-arrow-left"></i>
              </Button>
            )}
            <h5 className="mb-0">
              Factura {invoice.invoice_number}
            </h5>
            <Badge bg={si.variant}>{si.label}</Badge>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={handleDownloadPDF}>
              <i className="bi bi-file-earmark-pdf me-1"></i>Descargar PDF
            </Button>
            {isAdmin && invoice.status === 'draft' && (
              <Button variant="primary" size="sm" onClick={handleSendInvoice}>
                <i className="bi bi-send me-1"></i>Enviar
              </Button>
            )}
            {isAdmin && ['sent', 'partial_payment'].includes(invoice.status) && (
              <Button variant="success" size="sm" onClick={() => setShowPayment(true)}>
                <i className="bi bi-cash me-1"></i>Registrar Pago
              </Button>
            )}
            {isAdmin && ['draft', 'sent'].includes(invoice.status) && (
              <Button variant="outline-danger" size="sm" onClick={handleCancelInvoice}>
                <i className="bi bi-x-circle me-1"></i>Cancelar
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {/* Header info */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-2">
                <small className="text-muted d-block">Cliente</small>
                <strong>{invoice.project?.client?.company_name || '-'}</strong>
              </div>
              {invoice.project?.client?.legal_id && (
                <div className="mb-2">
                  <small className="text-muted d-block">Cedula Juridica</small>
                  <span>{invoice.project.client.legal_id}</span>
                </div>
              )}
            </Col>
            <Col md={6}>
              <Row>
                <Col xs={6}>
                  <small className="text-muted d-block">Fecha</small>
                  <strong>{formatDate(invoice.invoice_date)}</strong>
                </Col>
                <Col xs={6}>
                  <small className="text-muted d-block">Vencimiento</small>
                  <strong>{formatDate(invoice.due_date)}</strong>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col xs={6}>
                  <small className="text-muted d-block">Plazo</small>
                  <span>{invoice.payment_terms || 30} dias</span>
                </Col>
                <Col xs={6}>
                  <small className="text-muted d-block">Moneda</small>
                  <span>{invoice.currency || '—'}</span>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Items table */}
          {invoice.items && invoice.items.length > 0 && (
            <>
              <h6>Conceptos</h6>
              <Table size="sm" hover responsive className="mb-3">
                <thead className="bg-light">
                  <tr>
                    <th>Descripción</th>
                    <th>Tipo</th>
                    <th className="text-center">Cantidad</th>
                    <th>Unidad</th>
                    <th className="text-end">Precio Unit.</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td>
                        <Badge bg="light" text="dark">{item.item_type}</Badge>
                      </td>
                      <td className="text-center">{item.quantity}</td>
                      {/* #471: unit ahora puede ser NULL ("no se especificó").
                          Se muestra como "—", igual que en el PDF. */}
                      <td className={item.unit ? '' : 'text-muted'}>{item.unit || '—'}</td>
                      <td className="text-end">{formatCurrency(item.unit_price, invoice.currency)}</td>
                      <td className="text-end">{formatCurrency(item.total_price, invoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          {/* Totals */}
          <Row className="justify-content-end">
            <Col md={4}>
              <div className="bg-light p-3 rounded">
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal:</span>
                  <strong>{formatCurrency(invoice.subtotal, invoice.currency)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>IVA ({invoice.tax_rate}%):</span>
                  <strong>{formatCurrency(invoice.tax_amount, invoice.currency)}</strong>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-bold">Total:</span>
                  <strong className="text-primary fs-5">{formatCurrency(invoice.amount, invoice.currency)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-success">Pagado:</span>
                  <strong className="text-success">{formatCurrency(invoice.paid_amount, invoice.currency)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-danger">Pendiente:</span>
                  <strong className="text-danger">{formatCurrency(invoice.balance, invoice.currency)}</strong>
                </div>
              </div>
            </Col>
          </Row>

          {/* Payment history */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="mt-4">
              <h6>Historial de Pagos</h6>
              <Table size="sm" hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Metodo</th>
                    <th>Referencia</th>
                    <th className="text-end">Monto</th>
                    <th>Registrado Por</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{paymentMethodLabels[payment.payment_method] || payment.payment_method}</td>
                      <td>{payment.reference_number || '-'}</td>
                      <td className="text-end text-success fw-bold">{formatCurrency(payment.amount, invoice.currency)}</td>
                      <td>
                        <small className="text-muted">
                          {payment.created_by_profile?.full_name || '-'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {invoice.notes && (
            <div className="mt-3">
              <small className="text-muted d-block">Notas:</small>
              <p className="mb-0">{invoice.notes}</p>
            </div>
          )}
        </Card.Body>
      </Card>

      <PaymentModal
        show={showPayment}
        onHide={() => setShowPayment(false)}
        onSave={handleRecordPayment}
        balance={invoice.balance || 0}
        currency={invoice.currency}
        loading={paymentLoading}
      />
    </>
  );
};

export default InvoiceDetail;
